import HttpFactory from '~/repository/factory'
import type {
  CreateIntegrationClientResult,
  CreateWebhookSubscriptionResult,
  IntegrationClient,
  IntegrationClientPayload,
  IntegrationClientUpdate,
  IntegrationEvent,
  ListIntegrationClientsParams,
  ListWebhookDeliveriesParams,
  RotateIntegrationSecretResult,
  RotateWebhookSecretResult,
  UpdateIntegrationClientResult,
  UpdateWebhookSubscriptionResult,
  WebhookDeliveriesPage,
  WebhookDelivery,
  WebhookSubscription,
  WebhookSubscriptionPayload,
  WebhookSubscriptionUpdate,
} from '~/types/integration'

/**
 * AR-384 (UC099) — clientes de integração da API pública, sobre as rotas
 * admin da AR-383. O segredo devolvido por `create`/`rotateSecret` deve ficar
 * só em memória da página que o exibe (RN099-01, CLAUDE.md regra 8).
 */
class IntegracoesModule extends HttpFactory {
  private RESOURCE = '/admin/integration-clients'

  /** `?q=` busca por nome; `?ativo=` filtra o status. Sem paginação (RN099-04). */
  async list(params: ListIntegrationClientsParams = {}): Promise<IntegrationClient[]> {
    const query: Record<string, string> = {}
    if (params.q?.trim()) query.q = params.q.trim()
    if (params.ativo !== undefined) query.ativo = String(params.ativo)
    return this.get<IntegrationClient[]>(this.RESOURCE, { query })
  }

  async getById(id: string): Promise<IntegrationClient> {
    return this.get<IntegrationClient>(`${this.RESOURCE}/${id}`)
  }

  async create(payload: IntegrationClientPayload): Promise<CreateIntegrationClientResult> {
    return this.post<CreateIntegrationClientResult>(this.RESOURCE, payload)
  }

  /** Inclui ativar/desativar (`{ ativo }`) — a resposta traz `revoked_tokens`. */
  async update(id: string, payload: IntegrationClientUpdate): Promise<UpdateIntegrationClientResult> {
    return this.patch<UpdateIntegrationClientResult>(`${this.RESOURCE}/${id}`, payload)
  }

  async rotateSecret(id: string): Promise<RotateIntegrationSecretResult> {
    return this.post<RotateIntegrationSecretResult>(`${this.RESOURCE}/${id}/rotate-secret`)
  }

  // ── AR-385 (UC100) — webhooks do cliente, entregas e catálogo ──────────────

  async listWebhooks(clientId: string): Promise<WebhookSubscription[]> {
    return this.get<WebhookSubscription[]>(`${this.RESOURCE}/${clientId}/webhooks`)
  }

  /** O segredo HMAC devolvido aqui fica só em memória da página (RN100-01). */
  async createWebhook(
    clientId: string,
    payload: WebhookSubscriptionPayload,
  ): Promise<CreateWebhookSubscriptionResult> {
    return this.post<CreateWebhookSubscriptionResult>(`${this.RESOURCE}/${clientId}/webhooks`, payload)
  }

  /** Inclui pausar/reativar (`{ ativo }`). Removida → 409 `subscription_removed`. */
  async updateWebhook(
    clientId: string,
    whId: string,
    payload: WebhookSubscriptionUpdate,
  ): Promise<UpdateWebhookSubscriptionResult> {
    return this.patch<UpdateWebhookSubscriptionResult>(
      `${this.RESOURCE}/${clientId}/webhooks/${whId}`,
      payload,
    )
  }

  /** Remoção definitiva (RN04): pendentes canceladas, histórico preservado. */
  async removeWebhook(clientId: string, whId: string): Promise<void> {
    await this.delete<void>(`${this.RESOURCE}/${clientId}/webhooks/${whId}`)
  }

  async rotateWebhookSecret(clientId: string, whId: string): Promise<RotateWebhookSecretResult> {
    return this.post<RotateWebhookSecretResult>(
      `${this.RESOURCE}/${clientId}/webhooks/${whId}/rotate-secret`,
    )
  }

  /** Paginado e filtrável; a entrega de teste (ping) já vem fora (RN06). */
  async listDeliveries(
    clientId: string,
    whId: string,
    params: ListWebhookDeliveriesParams = {},
  ): Promise<WebhookDeliveriesPage> {
    const query: Record<string, string> = {}
    if (params.page !== undefined) query.page = String(params.page)
    if (params.page_size !== undefined) query.page_size = String(params.page_size)
    if (params.status) query.status = params.status
    if (params.from) query.from = params.from
    if (params.to) query.to = params.to
    return this.get<WebhookDeliveriesPage>(
      `${this.RESOURCE}/${clientId}/webhooks/${whId}/deliveries`,
      { query },
    )
  }

  /** Só `falhou`/`pausada` com subscrição e cliente ativos (RN07); senão 409. */
  async retryDelivery(deliveryId: string): Promise<WebhookDelivery> {
    return this.post<WebhookDelivery>(`/admin/webhook-deliveries/${deliveryId}/retry`)
  }

  /** Catálogo do servidor — a tela nunca tem lista fixa de eventos (RN03). */
  async listEvents(): Promise<IntegrationEvent[]> {
    return this.get<IntegrationEvent[]>('/admin/integration-events')
  }
}

export default IntegracoesModule
